// ObjectiveManagement.js - Version simplifiée sans concepts clés
// Module fonctionnel : Création, édition et gestion des objectifs

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Target, PenTool, Loader2, ArrowRight, Zap, Lightbulb, 
  GraduationCap, Briefcase, Heart, BookMarked, Compass, 
  Edit3, X, Plus, Clock, Menu
} from 'lucide-react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { askGPT } from '../components/OpenAIService';

// ============================================================================
// CONFIGURATION ET DONNÉES
// ============================================================================

const objectiveTypes = [
  { id: 'project', label: 'Projet', icon: <Target className="h-5 w-5" />, color: 'bg-purple-100 text-purple-700' },
  { id: 'idea', label: 'Idée', icon: <Lightbulb className="h-5 w-5" />, color: 'bg-yellow-100 text-yellow-700' },
  { id: 'skill', label: 'Compétence', icon: <GraduationCap className="h-5 w-5" />, color: 'bg-green-100 text-green-700' },
  { id: 'career', label: 'Carrière', icon: <Briefcase className="h-5 w-5" />, color: 'bg-blue-100 text-blue-700' },
  { id: 'learning', label: 'Apprentissage', icon: <BookMarked className="h-5 w-5" />, color: 'bg-indigo-100 text-indigo-700' },
  { id: 'desire', label: 'Désir', icon: <Heart className="h-5 w-5" />, color: 'bg-pink-100 text-pink-700' }
];

const formTips = {
  project: [
    "Quel est l'objectif principal de votre projet?",
    "Dans quel domaine s'inscrit-il (personnel, professionnel, éducatif)?",
    "Quel résultat concret souhaitez-vous obtenir?",
    "Quelles sont les étapes clés pour y parvenir?",
    "Quel est votre échéancier pour ce projet?"
  ],
  idea: [
    "Quelle est l'idée que vous aimeriez développer?",
    "Quel problème cherche-t-elle à résoudre?",
    "Qui pourrait bénéficier de cette idée?",
    "Quelles ressources seraient nécessaires pour concrétiser cette idée?",
    "Quels sont les défis potentiels à considérer?"
  ],
  skill: [
    "Quelle compétence souhaitez-vous développer ou améliorer?",
    "Pourquoi cette compétence est-elle importante pour vous?",
    "À quel niveau de maîtrise aspirez-vous?",
    "Comment comptez-vous mettre en pratique cette compétence?",
    "Quelles ressources pourriez-vous utiliser pour progresser?"
  ],
  career: [
    "Quel est votre objectif professionnel?",
    "Quelles étapes envisagez-vous pour y parvenir?",
    "Quelles compétences devrez-vous acquérir ou développer?",  
    "Quelles opportunités recherchez-vous?",
    "Comment cet objectif s'inscrit-il dans votre plan de carrière global?"
  ],
  learning: [
    "Quel sujet ou domaine souhaitez-vous explorer?",
    "Quel niveau de connaissance ou d'expertise visez-vous?",
    "Comment comptez-vous appliquer ces connaissances?",
    "Quelles méthodes d'apprentissage vous conviennent le mieux?",
    "Quelles ressources avez-vous déjà identifiées pour cet apprentissage?"
  ],
  desire: [
    "Qu'est-ce que vous souhaitez atteindre ou devenir?",
    "Pourquoi est-ce important pour vous?",
    "Quels changements cela apporterait dans votre vie?",
    "Quels obstacles pourraient vous empêcher d'y parvenir?",
    "Comment saurez-vous que vous avez atteint votre objectif?"
  ]
};

// ============================================================================
// SÉLECTEUR DE TYPE RESPONSIVE
// ============================================================================

const TypeSelector = ({ selectedType, onTypeSelect, onToggleTips, isMobile, isTablet }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  if (isMobile) {
    const selectedTypeData = objectiveTypes.find(type => type.id === selectedType);
    
    return (
      <div className="mb-6">
        <div className="text-center mb-4">
          <p className="text-sm font-medium text-gray-700">Parlez-moi de votre ambition… S'agit-il ?</p>
        </div>
        
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
              selectedTypeData 
                ? `${selectedTypeData.color} border-transparent` 
                : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex items-center gap-3">
              {selectedTypeData?.icon}
              <span className="font-medium">{selectedTypeData?.label || 'Choisir un type'}</span>
            </div>
            <Menu className={`h-5 w-5 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
          </button>
          
          {showDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-64 overflow-y-auto">
                {objectiveTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => {
                      onTypeSelect(type.id);
                      setShowDropdown(false);
                      onToggleTips(true);
                    }}
                    className={`w-full flex items-center gap-3 p-4 text-left transition-colors first:rounded-t-xl last:rounded-b-xl border-b border-gray-100 last:border-b-0 ${
                      selectedType === type.id 
                        ? 'bg-blue-50 text-blue-700 font-medium' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {type.icon}
                    <span>{type.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Version desktop/tablette
  return (
    <div className="mb-6">
      <div className="text-center mb-4">
        <p className="text-sm font-medium text-gray-700">Parlez-moi de votre ambition… S'agit-il ?</p>
      </div>
      <div className={`grid gap-3 ${isTablet ? 'grid-cols-3' : 'grid-cols-6'}`}>
        {objectiveTypes.map((type) => (
          <button
            key={type.id}
            type="button"
            onClick={() => {
              onTypeSelect(type.id);
              onToggleTips(true);
            }}
            className={`flex items-center justify-center gap-2 py-3 px-2 rounded-xl border-2 transition-all ${
              selectedType === type.id 
                ? `${type.color} border-transparent` 
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {type.icon}
            <span className="font-medium">{type.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// FORMULAIRE DE CRÉATION D'OBJECTIF
// ============================================================================

export const ObjectiveCreationForm = React.memo(({ onSubmit, onBack, isMobile, isTablet }) => {
  const [goal, setGoal] = useState('');
  const [selectedType, setSelectedType] = useState('project');
  const [isLoading, setIsLoading] = useState(false);
  const [showTips, setShowTips] = useState(false);
  
  const goalTextareaRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      goalTextareaRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleGoalChange = useCallback((e) => {
    setGoal(e.target.value);
  }, []);

  const handleTypeSelection = useCallback((typeId) => {
    setSelectedType(typeId);
    setShowTips(true);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!goal.trim() || isLoading) return;
    
    setIsLoading(true);
    
    try {
      await onSubmit({
        objectif: goal.trim(),
        type: selectedType
      });
    } catch (error) {
      console.error("Erreur lors de la création de l'objectif:", error);
    } finally {
      setIsLoading(false);
    }
  }, [goal, selectedType, isLoading, onSubmit]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Sélection du type d'objectif */}
      <TypeSelector 
        selectedType={selectedType}
        onTypeSelect={handleTypeSelection}
        onToggleTips={setShowTips}
        isMobile={isMobile}
        isTablet={isTablet}
      />
      
      {/* Champ de saisie */}
      <div className="relative">
        <div className={`absolute ${isMobile ? 'left-3 top-3' : 'left-4 top-4'} flex items-center`}>
          <PenTool className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400`} />
        </div>
        <textarea
          ref={goalTextareaRef}
          value={goal}
          onChange={handleGoalChange}
          placeholder={`Décrivez votre ${
            selectedType === 'project' ? 'projet' : 
            selectedType === 'idea' ? 'idée' : 
            selectedType === 'desire' ? 'désir' :
            selectedType === 'skill' ? 'compétence à acquérir' :
            selectedType === 'career' ? 'objectif de carrière' : 'objectif d\'apprentissage'
          } en quelques mots. Notez vos idées comme elles viennent.`}
          className={`w-full ${
            isMobile ? 'pl-10 pr-3 py-3 h-40 text-base' : 'pl-12 pr-4 py-4 h-56 text-lg'
          } rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-colors resize-none`}
          required
        />
        <div className={`flex justify-between text-xs text-gray-500 mt-1 ${isMobile ? 'px-1' : 'px-2'}`}>
          <span>{goal.length} caractères</span>
          <span>{goal.length < 10 ? "Minimum 10 caractères recommandés" : "👍 Bonne longueur"}</span>
        </div>
      </div>
      
      {/* Guide de formulation */}
      {showTips && selectedType && formTips[selectedType] && (
        <div className={`bg-blue-50 rounded-xl ${isMobile ? 'p-3' : 'p-4'} border border-blue-100`}>
          <div className="flex items-start">
            <Zap className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-blue-600 flex-shrink-0 mt-0.5`} />
            <div className="ml-2">
              <h4 className={`font-medium text-blue-700 ${isMobile ? 'text-sm' : ''}`}>
                Guide pour formuler votre {
                  selectedType === 'project' ? 'projet' : 
                  selectedType === 'idea' ? 'idée' : 
                  selectedType === 'desire' ? 'désir' :
                  selectedType === 'skill' ? 'objectif de compétence' :
                  selectedType === 'career' ? 'objectif de carrière' : 'objectif d\'apprentissage'
                }
              </h4>
              <ul className={`mt-2 space-y-1 ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>
                {formTips[selectedType].map((tip, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {/* Bouton de soumission */}
      <div className="flex justify-center">
        <button
          type="submit"
          disabled={!goal.trim() || goal.length < 10 || isLoading}
          className={`flex items-center justify-center ${
            isMobile ? 'px-6 py-3 text-base w-full' : 'px-8 py-4 text-lg'
          } rounded-xl font-medium transition-all duration-300 ${
            !goal.trim() || goal.length < 10 || isLoading 
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
              : 'bg-gradient-to-r from-purple-600 to-indigo-700 text-white shadow-lg hover:shadow-xl hover:translate-y-[-2px]'
          }`}
        >
          {isLoading ? (
            <>
              <Loader2 className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} animate-spin mr-2`} />
              {isMobile ? 'Analyse...' : 'Analyse en cours...'}
            </>
          ) : (
            <>
              {isMobile ? 'Découvrir' : 'Découvrir mon potentiel'} 
              <ArrowRight className={`ml-2 ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
            </>
          )}
        </button>
      </div>
    </form>
  );
});

ObjectiveCreationForm.displayName = 'ObjectiveCreationForm';

// ============================================================================
// AFFICHAGE ET ÉDITION D'OBJECTIF AVEC TOUTES LES FONCTIONNALITÉS
// ============================================================================

// Fonction utilitaire pour gérer les dates de manière sécurisée
const getSafeDate = (timestamp) => {
  if (!timestamp) return new Date();
  if (typeof timestamp.toDate === 'function') return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === 'string' || typeof timestamp === 'number') return new Date(timestamp);
  return new Date();
};

export const ObjectiveView = React.memo(({ 
  objective, 
  userObjectives, 
  onSelectObjective, 
  onNavigateToStrategy, 
  onCreateObjective,
  onUpdateObjective,
  userId,
  isMobile,
  processedObjective,
  processedOtherObjectives,
  getDomainInfo
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempObjective, setTempObjective] = useState(objective?.objectifOriginal || objective?.title || '');
  const [isUpdating, setIsUpdating] = useState(false);

  // Mettre à jour tempObjective quand objective change
  useEffect(() => {
    setTempObjective(objective?.objectifOriginal || objective?.title || '');
  }, [objective]);

  const handleSaveObjective = async () => {
    if (!tempObjective.trim() || !objective?.id) return;
    
    try {
      setIsUpdating(true);
      
      // Régénérer le titre et la description avec l'IA
      const regenerationPrompt = `En tant qu'expert en développement personnel et professionnel, analyse cet objectif utilisateur et génère une structure mise à jour.

Objectif utilisateur modifié : "${tempObjective.trim()}"

Génère une réponse au format JSON strict avec cette structure :
{
  "title": "Titre concis et motivant de l'objectif (max 80 caractères)",
  "description": "Description structurée et inspirante de l'objectif",
  "chapitre": "Domaine principal parmi : Professionnel, Personnel, Educatif, Business, Activités ludique et créative, Développement entrepreneurial, Formation et apprentissage, Évolution de carrière, Santé et bien-être, Objectifs Personnels, Voyages, Gestion immobilière, Finance et Banque, Activités sportives"
}

Assure-toi que :
- Le titre soit accrocheur et motivant
- La description inspire l'action
- Le domaine corresponde exactement à un des domaines listés`;

      const regenerationResult = await askGPT(regenerationPrompt);
      
      // Parser la réponse JSON
      let updatedData = {
        title: tempObjective.trim(),
        description: tempObjective.trim(),
        chapitre: objective.chapitre || 'Personnel'
      };
      
      try {
        const jsonMatch = regenerationResult.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          updatedData = {
            title: parsed.title || tempObjective.trim(),
            description: parsed.description || tempObjective.trim(),
            chapitre: parsed.chapitre || objective.chapitre || 'Personnel'
          };
        }
      } catch (parseError) {
        console.error('Erreur parsing JSON régénération:', parseError);
        // Utiliser les valeurs par défaut si le parsing échoue
      }

      // Mettre à jour en base de données
      await updateDoc(doc(db, 'users', userId, 'competences', objective.id), {
        objectifOriginal: tempObjective.trim(),
        title: updatedData.title,
        description: updatedData.description,
        chapitre: updatedData.chapitre,
        updatedAt: serverTimestamp(),
        // Réinitialiser la recommandation pour régénération
        recommendation: null,
        actionPlan: null,
        lastAIUpdate: new Date().toISOString()
      });
      
      if (onUpdateObjective) {
        onUpdateObjective({
          ...objective,
          objectifOriginal: tempObjective.trim(),
          title: updatedData.title,
          description: updatedData.description,
          chapitre: updatedData.chapitre
        });
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour. Veuillez réessayer.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setTempObjective(objective?.objectifOriginal || objective?.title || '');
    setIsEditing(false);
  };
  
  return (
    <div className="space-y-6">
      {/* Carte "Votre objectif" avec édition */}
      <div className={`bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg border border-gray-200 ${isMobile ? 'p-4' : 'p-6'}`}>
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <Compass className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} text-blue-600`} />
            <div>
              <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-800`}>Votre objectif</h3>
              <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>
                {isEditing ? 'Modification du texte original' : 'Ambition définie'}
              </p>
            </div>
          </div>
          
          <button
            onClick={() => setIsEditing(!isEditing)}
            className={`${isMobile ? 'p-1' : 'p-2'} hover:bg-blue-100 rounded-full transition-colors`}
            title={isEditing ? "Annuler" : "Modifier l'objectif"}
            disabled={isUpdating}
          >
            {isEditing ? 
              <X className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-gray-600`} /> : 
              <Edit3 className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-blue-600`} />
            }
          </button>
        </div>

        {/* Zone d'édition ou affichage */}
        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Texte original de votre objectif :
              </label>
              <textarea
                value={tempObjective}
                onChange={(e) => setTempObjective(e.target.value)}
                className={`w-full ${isMobile ? 'p-3 text-base' : 'p-4 text-lg'} border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white`}
                rows={isMobile ? 3 : 4}
                placeholder="Modifiez votre objectif original..."
                disabled={isUpdating}
              />
              <p className="text-xs text-gray-500 mt-1">
                <strong>Important :</strong> La modification régénérera automatiquement le titre, la description et les recommandations.
              </p>
            </div>
            
            {isUpdating && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <div className="flex items-center">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600 mr-2" />
                  <p className="text-sm text-blue-700">
                    Régénération du titre et de la description en cours...
                  </p>
                </div>
              </div>
            )}
            
            <div className={`flex ${isMobile ? 'flex-col gap-2' : 'justify-end gap-3'}`}>
              <button
                onClick={handleCancelEdit}
                disabled={isUpdating}
                className={`${isMobile ? 'px-4 py-2 text-sm' : 'px-4 py-2'} text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50`}
              >
                Annuler
              </button>
              <button
                onClick={handleSaveObjective}
                disabled={!tempObjective.trim() || isUpdating}
                className={`${isMobile ? 'px-6 py-2 text-sm' : 'px-6 py-2'} bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center`}
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Mise à jour...
                  </>
                ) : (
                  'Sauvegarder et régénérer'
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className={`bg-white bg-opacity-70 ${isMobile ? 'p-3' : 'p-4'} rounded-lg border border-gray-100`}>
            {/* Affichage du texte original */}
            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-1">Votre texte original :</p>
              <p className={`${isMobile ? 'text-sm' : 'text-base'} text-gray-800 font-medium`}>
                {processedObjective.objectifOriginal || processedObjective.title}
              </p>
            </div>
            
            {/* Affichage du titre généré si différent */}
            {processedObjective.objectifOriginal && 
             processedObjective.objectifOriginal !== processedObjective.title && (
              <div className="mb-3 pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Titre généré par l'IA :</p>
                <p className={`${isMobile ? 'text-sm' : 'text-base'} text-gray-700`}>
                  {processedObjective.title}
                </p>
              </div>
            )}
            
            {/* Affichage de la description si différente */}
            {processedObjective.description && 
             processedObjective.description !== processedObjective.title && 
             processedObjective.description !== processedObjective.objectifOriginal && (
              <div className="mb-3 pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Description générée :</p>
                <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''}`}>
                  {processedObjective.description}
                </p>
              </div>
            )}
            
            {/* Indication de dernière mise à jour IA */}
            {processedObjective.lastAIUpdate && (
              <div className="mb-2">
                <p className="text-xs text-green-600">
                  Dernière mise à jour IA : {new Date(processedObjective.lastAIUpdate).toLocaleString()}
                </p>
              </div>
            )}
            
            {/* Tags d'information */}
            <div className={`flex flex-wrap gap-2 mt-3 pt-2 border-t border-gray-200 ${isMobile ? 'text-xs' : ''}`}>
              {processedObjective.type && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  processedObjective.type === 'project' ? 'bg-purple-100 text-purple-700' :
                  processedObjective.type === 'idea' ? 'bg-yellow-100 text-yellow-700' :
                  processedObjective.type === 'desire' ? 'bg-pink-100 text-pink-700' :
                  processedObjective.type === 'skill' ? 'bg-green-100 text-green-700' :
                  processedObjective.type === 'career' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                } ${isMobile ? 'text-xs px-2 py-1' : ''}`}>
                  {processedObjective.type === 'project' ? 'Projet' :
                   processedObjective.type === 'idea' ? 'Idée' :
                   processedObjective.type === 'desire' ? 'Désir' :
                   processedObjective.type === 'skill' ? 'Compétence' :
                   processedObjective.type === 'career' ? 'Carrière' : 'Objectif'}
                </span>
              )}
              
              {(processedObjective.chapitre || processedObjective.theme) && (
                <span className={`px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm ${isMobile ? 'text-xs px-2 py-1 max-w-24 truncate' : ''}`}>
                  {processedObjective.chapitre || processedObjective.theme}
                </span>
              )}
              
              <span className={`px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm flex items-center gap-1 ${isMobile ? 'text-xs px-2 py-1' : ''}`}>
                <Zap className="w-3 h-3" />
                {processedObjective.completedActionsCount}/{processedObjective.actionsCount} actions
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Actions principales - BOUTONS STRATÉGIE ET NOUVEL OBJECTIF */}
      <div className={`bg-white rounded-xl shadow-lg border border-gray-200 ${isMobile ? 'p-4' : 'p-6'}`}>
        <div className={`flex ${isMobile ? 'flex-col' : 'flex-col sm:flex-row'} gap-4 justify-center`}>
          <button
            onClick={onNavigateToStrategy}
            className={`${isMobile ? 'px-4 py-3 text-sm' : 'px-6 py-3'} bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-colors flex items-center justify-center gap-2`}
          >
            {isMobile ? 'Stratégie' : 'Voir la stratégie'}
            <ArrowRight className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
          </button>
          
          <button
            onClick={onCreateObjective}
            className={`${isMobile ? 'px-4 py-3 text-sm' : 'px-6 py-3'} bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-colors flex items-center justify-center gap-2`}
          >
            <Plus className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
            {isMobile ? 'Nouvel objectif' : 'Créer un nouvel objectif'}
          </button>
        </div>
      </div>

      {/* Objectifs récents - SECTION VOS AUTRES OBJECTIFS RÉCENTS */}
      {processedOtherObjectives.length > 0 && (
        <div className={`bg-gray-50 rounded-xl ${isMobile ? 'p-4' : 'p-6'}`}>
          <div className="flex justify-between items-center mb-4">
            <h4 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-800 flex items-center`}>
              <Clock className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} mr-2 text-blue-500`} />
              {isMobile ? 'Autres objectifs' : 'Vos autres objectifs récents'}
            </h4>
            <button
              onClick={() => onSelectObjective('list')}
              className={`text-blue-600 hover:text-blue-800 ${isMobile ? 'text-xs' : 'text-sm'} font-medium flex items-center gap-1`}
            >
              {isMobile ? 'Tous' : 'Voir tous les objectifs'}
              <ArrowRight className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
            </button>
          </div>
          
          <div className="space-y-3">
            {processedOtherObjectives.map((otherObjective) => {
              const otherDomainInfo = getDomainInfo(otherObjective.chapitre || otherObjective.theme);
              return (
                <div 
                  key={otherObjective.id}
                  onClick={() => onSelectObjective(otherObjective)}
                  className={`flex items-center ${isMobile ? 'p-2' : 'p-3'} bg-white rounded-lg hover:bg-blue-50 cursor-pointer transition-colors border border-gray-200 hover:border-blue-300`}
                >
                  <div className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} bg-blue-100 rounded-lg flex items-center justify-center ${isMobile ? 'mr-2' : 'mr-3'}`}>
                    {otherDomainInfo.iconSmall}
                  </div>
                  <div className="flex-grow min-w-0">
                    <h5 className={`font-medium text-gray-800 ${isMobile ? 'text-sm' : ''} truncate`}>{otherObjective.title}</h5>
                    <div className={`flex items-center gap-2 ${isMobile ? 'text-xs' : 'text-xs'} text-gray-500 mt-1 flex-wrap`}>
                      {(otherObjective.chapitre || otherObjective.theme) && (
                        <span className={`px-2 py-1 bg-blue-100 text-blue-700 rounded ${isMobile ? 'truncate max-w-20' : ''}`}>
                          {otherObjective.chapitre || otherObjective.theme}
                        </span>
                      )}
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        {otherObjective.completedActionsCount}/{otherObjective.actionsCount}
                      </span>
                    </div>
                    <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 mt-1`}>
                      {otherObjective.updatedAt ? 
                        `Modifié le ${getSafeDate(otherObjective.updatedAt).toLocaleDateString()}` :
                        'Récent'
                      }
                    </p>
                  </div>
                  <ArrowRight className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-gray-400 flex-shrink-0`} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

ObjectiveView.displayName = 'ObjectiveView';
